import React, { useEffect, useState, useRef } from 'react';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';

/**
 * Polls `/api/health` every 3s. When polling fails (or times out) for more
 * than UNHEALTHY_THRESHOLD_MS, shows a banner pinned to the top of the
 * viewport: "Server is restarting — saves paused. Please wait." When health
 * is restored, shows a brief green "Back online" confirmation before
 * hiding. Uses raw `fetch` (no axios interceptor → no token refresh /
 * 401 redirect during a restart).
 */
const POLL_INTERVAL_MS = 3000;
const UNHEALTHY_THRESHOLD_MS = 5000;
const REQUEST_TIMEOUT_MS = 4000;
const RESTORED_FLASH_MS = 4000;

const BackendHealthBanner = () => {
  const [unhealthy, setUnhealthy] = useState(false);
  const [restoredAt, setRestoredAt] = useState(null);
  const firstFailureRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    let timer;

    const poll = async () => {
      const controller = new AbortController();
      const abortTimer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      let ok = false;
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/health`;
        const res = await fetch(url, { signal: controller.signal, cache: 'no-store' });
        ok = res.ok;
      } catch {
        ok = false;
      } finally {
        clearTimeout(abortTimer);
      }
      if (cancelled) return;

      if (ok) {
        if (firstFailureRef.current) {
          // Was unhealthy → now restored. Flash the "Back online" pill.
          firstFailureRef.current = null;
          if (unhealthy) {
            setUnhealthy(false);
            setRestoredAt(Date.now());
            setTimeout(() => { if (!cancelled) setRestoredAt(null); }, RESTORED_FLASH_MS);
          }
        }
      } else {
        if (!firstFailureRef.current) firstFailureRef.current = Date.now();
        const downFor = Date.now() - firstFailureRef.current;
        if (downFor >= UNHEALTHY_THRESHOLD_MS && !unhealthy) {
          setUnhealthy(true);
        }
      }
      timer = setTimeout(poll, POLL_INTERVAL_MS);
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [unhealthy]);

  if (!unhealthy && !restoredAt) return null;

  if (unhealthy) {
    return (
      <div
        data-testid="backend-health-banner-down"
        className="fixed top-0 left-0 right-0 z-[100] bg-amber-500/95 text-amber-950 backdrop-blur supports-[backdrop-filter]:bg-amber-500/90 border-b border-amber-600 shadow-lg"
      >
        <div className="flex items-center justify-center gap-3 px-4 py-2.5 text-sm font-medium">
          <AlertTriangle className="h-4 w-4 shrink-0 animate-pulse" />
          <span>
            Server is restarting — your saves are paused. Please wait a moment, then click <strong>Save</strong> only once.
          </span>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="backend-health-banner-up"
      className="fixed top-0 left-0 right-0 z-[100] bg-emerald-500/95 text-emerald-950 backdrop-blur supports-[backdrop-filter]:bg-emerald-500/90 border-b border-emerald-600 shadow-lg"
    >
      <div className="flex items-center justify-center gap-3 px-4 py-2.5 text-sm font-medium">
        <CheckCircle2 className="h-4 w-4 shrink-0" />
        <span>Back online. You can save again.</span>
      </div>
    </div>
  );
};

export default BackendHealthBanner;
